import {
  Component,
  computed,
  inject,
  Injector,
  input,
  signal,
} from '@angular/core';
import { NonNullableFormBuilder, ReactiveFormsModule } from '@angular/forms';
import { TranslatePipe } from '@ngx-translate/core';
import type {
  OnyxAmount,
  OnyxAmountForm,
  OnyxHttpErrorResponse,
} from '@onyx/angular';
import {
  OnyxButtonComponent,
  OnyxDividerComponent,
  OnyxFormMode,
  OnyxInformationHeadingComponent,
  OnyxToastService,
} from '@onyx/angular';
import { ContractorShippingMethodsComponent } from '../../../../../../../common/components/contractor-shipping-methods/contractor-shipping-methods.component';
import { VALIDATION_ERROR_CODES } from '../../../../../../../common/constants/common/validation-error-codes';
import { ValidationHelper } from '../../../../../../../common/helpers/validation.helper';
import { PreferencesService } from '../../../../../../../common/services/preferences.service';
import { OrderStatus } from '../../../../../common/enums/order-status';
import { OrderHelper } from '../../../../../common/helpers/order.helper';
import type {
  Order,
  OrderPaymentHistoryItem,
} from '../../../../../common/interfaces/order';
import { OrdersService } from '../../../../../common/services/orders.service';
import { OrderModalPaymentFormComponent } from '../order-modal-payment-form/order-modal-payment-form.component';
import { OrderModalPaymentsHistoryComponent } from '../order-modal-payments-history/order-modal-payments-history.component';

export type OrderModalPaymentDto = {
  date: string | null;
  net: OnyxAmountForm | null;
  vat: OnyxAmountForm | null;
};

export interface OrderPaymentHistoryItemData extends OrderPaymentHistoryItem {
  netPercentage: number | null;
  remainingNet: OnyxAmount;
  vatPercentage: number | null;
  remainingVat: OnyxAmount;
}

@Component({
  selector: 'app-order-modal-payment-form-container',
  imports: [
    ReactiveFormsModule,
    OnyxButtonComponent,
    OrderModalPaymentsHistoryComponent,
    TranslatePipe,
    OrderModalPaymentFormComponent,
    ContractorShippingMethodsComponent,
    OnyxInformationHeadingComponent,
    OnyxDividerComponent,
  ],
  templateUrl: './order-modal-payment-form-container.component.html',
  styleUrl: './order-modal-payment-form-container.component.scss',
})
export class OrderModalPaymentFormContainerComponent {
  private readonly fb = inject(NonNullableFormBuilder);
  private readonly toastService = inject(OnyxToastService);
  private readonly ordersService = inject(OrdersService);
  private readonly preferencesService = inject(PreferencesService);
  private readonly injector = inject(Injector);

  public readonly order = input.required<Order>();

  protected readonly I18N = 'orders.orderModal';

  protected readonly OrderStatus = OrderStatus;
  protected readonly OnyxFormMode = OnyxFormMode;

  protected form = OrderModalPaymentFormComponent.buildForm(
    this.fb,
    this.preferencesService,
  );
  protected loading = signal(false);
  protected isPartiallyPaid = signal(false);

  protected paymentDetails = computed(() =>
    OrderHelper.getPaymentDetails(this.order().payment),
  );

  protected async submit(): Promise<void> {
    if (!(await ValidationHelper.checkValidity(this.form, this.injector))) {
      return;
    }

    const form = this.form.getRawValue();
    const { net, vat } = form;

    const details = this.isPartiallyPaid() ? null : this.paymentDetails();
    const dto: OrderModalPaymentDto = {
      ...form,
      net: net?.value
        ? (net as OnyxAmountForm)
        : (details?.invoiceNetAmount ?? null),
      vat: vat?.value
        ? (vat as OnyxAmountForm)
        : (details?.invoiceVatAmount ?? null),
    };

    if (!dto.net && !dto.vat && this.isPartiallyPaid()) {
      this.toastService.showError({
        message: `${this.I18N}.toasts.requiredOneField`,
      });
      return;
    }

    this.loading.set(true);
    this.ordersService
      .addPayment(this.order().uuid, dto)
      .subscribe({
        next: () => {
          if (dto.net || dto.vat) {
            const isFullyPaid =
              dto.net?.value === this.paymentDetails()?.remainingNet &&
              dto.vat?.value === this.paymentDetails()?.remainingVat;

            this.toastService.showSuccess({
              message: `${this.I18N}.toasts.${isFullyPaid ? 'paid' : 'partiallyPaid'}`,
            });
          } else {
            this.toastService.showSuccess({
              message: `${this.I18N}.toasts.paid`,
            });
          }
        },
        error: (response: OnyxHttpErrorResponse) => {
          if (!VALIDATION_ERROR_CODES.includes(response.status)) throw response;

          const ERRORS = {
            'payment.net': {
              message:
                'Sum of all net payments can not exceed net value of invoice.',
              key: 'orders.toasts.netPaymentsExceedInvoice',
            },
            'payment.vat': {
              message:
                'Sum of all vat payments can not exceed vat value of invoice.',
              key: 'orders.toasts.vatPaymentsExceedInvoice',
            },
          };

          for (const [field, { message, key }] of Object.entries(ERRORS)) {
            if (response.error?.[field] === message) {
              this.toastService.showError({ message: key });
              response.handled = true;
            }
          }

          throw response;
        },
      })
      .add(() => this.loading.set(false));
  }

  protected deletePayment(uuid: string) {
    this.ordersService.deletePayment(this.order().uuid, uuid).subscribe(() =>
      this.toastService.showSuccess({
        message: `${this.I18N}.toasts.paymentDeleted`,
        keepOnNavigation: true,
      }),
    );
  }
}
